import { useState, type FormEvent } from "react";
import { Brand } from "../../components/Brand";
import { LockIcon, SafetyIcon } from "../../components/Icons";
import { api } from "../../lib/api";
import { readableError } from "../../lib/errors";

interface SetupScreenProps {
  onComplete: () => void;
  onCancel: () => void;
  setupToken?: string;
}

type RecoveryCodes = { wearer: string; controller: string };

export function SetupScreen({
  onComplete,
  onCancel,
  setupToken = ""
}: SetupScreenProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [codes, setCodes] = useState<RecoveryCodes | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      const result = await api.setup(
        {
          wearer: {
            displayName: String(data.get("wearerName")),
            email: String(data.get("wearerEmail")),
            password: String(data.get("wearerPassword"))
          },
          controller: {
            displayName: String(data.get("controllerName")),
            email: String(data.get("controllerEmail")),
            password: String(data.get("controllerPassword"))
          }
        },
        setupToken
      );
      setCodes(result.recoveryCodes);
    } catch (cause) {
      setError(readableError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (codes) {
    return (
      <main className="setup-screen">
        <Brand />
        <section className="recovery-codes">
          <LockIcon />
          <h1>Save both recovery codes</h1>
          <p>
            They are shown once. Store each code separately in the account owner’s
            password manager.
          </p>
          <div className="recovery-codes__grid">
            <div>
              <span>Wearer recovery code</span>
              <code>{codes.wearer}</code>
            </div>
            <div>
              <span>Controller recovery code</span>
              <code>{codes.controller}</code>
            </div>
          </div>
          <button className="button button--primary" onClick={onComplete}>
            I HAVE SAVED BOTH CODES
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="setup-screen">
      <div className="setup-screen__header">
        <Brand />
        <button className="text-button" onClick={onCancel}>Back to sign in</button>
      </div>
      <section className="setup-intro">
        <h1>Two protected roles.<br />No shared password.</h1>
        <p>
          The wearer owns Bluetooth, limits, arming, and revocation. The
          controller can act only inside those limits.
        </p>
      </section>

      <form className="setup-form" onSubmit={submit}>
        <AccountFields
          title="Wearer account"
          description="Connects the collar and owns the stop."
          prefix="wearer"
        />
        <AccountFields
          title="Controller account"
          description="Receives only the control the wearer grants."
          prefix="controller"
        />
        <div className="setup-form__submit">
          <SafetyIcon />
          <p>
            Roles are locked after setup. Passwords require at least 6
            characters.
          </p>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button button--primary" disabled={busy}>
            {busy ? "CREATING PRIVATE SPACE…" : "CREATE PRIVATE SPACE"}
          </button>
        </div>
      </form>
    </main>
  );
}

function AccountFields({
  title,
  description,
  prefix
}: {
  title: string;
  description: string;
  prefix: "wearer" | "controller";
}) {
  return (
    <fieldset className="account-fields">
      <legend>{title}</legend>
      <p>{description}</p>
      <label>
        <span>Display name</span>
        <input
          name={`${prefix}Name`}
          autoComplete={`section-${prefix} name`}
          required
        />
      </label>
      <label>
        <span>Email</span>
        <input
          name={`${prefix}Email`}
          type="email"
          inputMode="email"
          autoCapitalize="none"
          autoComplete={`section-${prefix} email`}
          spellCheck={false}
          required
        />
      </label>
      <label>
        <span>Password</span>
        <input
          name={`${prefix}Password`}
          type="password"
          autoComplete={`section-${prefix} new-password`}
          minLength={6}
          required
        />
      </label>
    </fieldset>
  );
}
